import { Text, VStack } from '@chakra-ui/react';
import { addMonths, format } from 'date-fns';

import { useOnceWhen } from '../../../../../../hooks';
import { trackParkedViewed } from '../../../../../../services/analytics/marketplaceEvents';
import { formatMoney } from '../../../../../../utils/helpers';
import MarketplaceCard from '../../MarketplaceCard';
import { LeadGuidance, ParkedPath, getBuyoutTaxLine } from '../../guidance';
import BuyoutCallButton from '../waiting/BuyoutCallButton';

type ParkedPathPanelProps = {
  path: ParkedPath;
  guidance: LeadGuidance;
  dealId: string;
  leadId: string;
  model?: string | null;
};

const BuyoutPanelBody = ({
  guidance,
  dealId,
  leadId,
  model,
}: Omit<ParkedPathPanelProps, 'path'>) => {
  const buyoutAmount =
    guidance.buyout != null ? formatMoney(guidance.buyout.payoff, { noCents: true }) : null;

  return (
    <>
      <Text fontSize={{ base: '22px', md: '26px' }} fontWeight="700" color="leaseEndBlue">
        Keep your car.
      </Text>
      <Text fontSize="13.5px" color="charcoal" lineHeight="1.6" maxW="46ch">
        Own your {model || 'car'}
        {buyoutAmount != null && (
          <>
            {' '}
            for about{' '}
            <Text as="b" color="leaseEndBlue">
              {buyoutAmount}
            </Text>
          </>
        )}
        . Our buyout team walks you through your exact total, financing, and next steps, and it
        starts with one call.
      </Text>
      {guidance.buyout != null && (
        <Text fontSize="11.5px" color="grayLight" lineHeight="1.5">
          {getBuyoutTaxLine(guidance.buyout.taxAndFees)}
        </Text>
      )}
      <BuyoutCallButton source="parked_buyout" dealId={dealId} leadId={leadId} mt="6px" />
      <Text fontSize="12px" color="grayLight" lineHeight="1.5" maxW="46ch">
        Usually a few minutes on the phone. We'll walk you through financing options and next steps.
      </Text>
    </>
  );
};

const FinishLeasePanelBody = ({ remainingMonths }: { remainingMonths: number | null }) => (
  <>
    <Text
      fontSize={{ base: '22px', md: '26px' }}
      fontWeight="700"
      color="leaseEndBlue"
      lineHeight="1.2"
    >
      {remainingMonths != null
        ? `Keep driving. Hand the keys back ${format(
            addMonths(new Date(), remainingMonths),
            'MMM yyyy',
          )}.`
        : 'Keep driving until lease end.'}
    </Text>
    <Text fontSize="13.5px" color="charcoal" lineHeight="1.6" maxW="46ch">
      Nothing you need to do here. And as lease end gets closer, your options stay open: sell, buy
      out, or hand it back.
    </Text>
  </>
);

const ParkedPathPanel = ({ path, guidance, dealId, leadId, model }: ParkedPathPanelProps) => {
  useOnceWhen(leadId !== '', () => trackParkedViewed(dealId, leadId, path));

  const stripTitle = path === 'buyout' ? 'Your buyout' : 'Finishing your lease';

  return (
    <MarketplaceCard header={stripTitle} h="100%">
      <VStack
        spacing={4}
        align="center"
        justify="center"
        textAlign="center"
        flex="1"
        px={{ base: '18px', md: '32px' }}
        py={{ base: '28px', md: '40px' }}
      >
        {path === 'buyout' ? (
          <BuyoutPanelBody guidance={guidance} dealId={dealId} leadId={leadId} model={model} />
        ) : (
          <FinishLeasePanelBody remainingMonths={guidance.remainingMonths} />
        )}
      </VStack>
    </MarketplaceCard>
  );
};

export default ParkedPathPanel;
